import { useState, useEffect } from 'react';
import { Star, X } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const ratingLabels = ['', 'Terrible', 'Poor', 'Average', 'Good', 'Excellent'];

/**
 * ConsumerReviewDrawer
 * Right-side drawer for a consumer to view or edit one of their own reviews.
 *
 * Props:
 *  - review: object | null  — the review data
 *  - mode: 'view' | 'edit'  (default: 'view')
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onReviewUpdated: () => void  — called after a successful edit
 */
const ConsumerReviewDrawer = ({ review, mode = 'view', isOpen, onClose, onReviewUpdated }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [experienceDate, setExperienceDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Populate form fields when drawer opens with a review
  useEffect(() => {
    if (isOpen && review) {
      setTitle(review.title || '');
      setContent(review.content || '');
      setRating(review.rating || 0);
      setHoveredRating(0);
      setExperienceDate(
        review.experience_date ? review.experience_date.split('T')[0] : ''
      );
      setError('');
    }
  }, [isOpen, review]);

  if (!isOpen || !review) return null;

  const displayRating = hoveredRating || rating;

  const StarRowStatic = ({ value }) => (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={20}
          className={i <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'fill-slate-100 text-slate-300'}
        />
      ))}
    </span>
  );

  const validate = () => {
    if (rating < 1) return 'Please select a star rating (1–5).';
    if (!title.trim() || title.trim().length < 5) return 'Title must be at least 5 characters.';
    if (!content.trim() || content.trim().length < 10) return 'Review content must be at least 10 characters.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/consumer/reviews/${review.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          rating,
          experience_date: experienceDate || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update review');
      onReviewUpdated?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const businessName = review.business?.business_name || 'Business';
  const businessCity = review.business?.city;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0 bg-white">
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900">
              {mode === 'edit' ? 'Edit Review' : 'Review Details'}
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[360px]">
              {businessName}
              {businessCity && <span className="text-slate-400"> · {businessCity}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Close drawer"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── VIEW MODE ── */}
        {mode === 'view' ? (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

            {/* Rating */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-3">Rating</h3>
              <div className="flex items-center gap-3">
                <StarRowStatic value={review.rating} />
                <span className="text-[13px] font-semibold text-slate-700">{review.rating}.0 out of 5</span>
              </div>
            </div>

            {/* Status */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Status</h3>
              {review.is_approved ? (
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-[12px] font-medium text-emerald-700">
                  ✓ Approved
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[12px] font-medium text-amber-700">
                  ◷ Pending Approval
                </span>
              )}
            </div>

            {/* Title */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Title</h3>
              <p className="text-[13px] text-slate-800 leading-relaxed">{review.title || '—'}</p>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Your Review</h3>
              <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{review.content || '—'}</p>
            </div>

            {/* Experience Date */}
            {review.experience_date && (
              <div>
                <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Experience Date</h3>
                <p className="text-[13px] text-slate-600">{formatDate(review.experience_date)}</p>
              </div>
            )}

            {/* Helpfulness */}
            {/* <div className="flex items-center gap-8 pt-3 border-t border-slate-200">
              <div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide block mb-0.5">Helpful votes</span>
                <span className="text-[16px] font-bold text-emerald-600">{review.helpful_count ?? 0}</span>
              </div>
              <div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide block mb-0.5">Not helpful</span>
                <span className="text-[16px] font-bold text-slate-500">{review.unhelpful_count ?? 0}</span>
              </div>
            </div> */}

            {/* Timestamps */}
            <div className="text-[11px] text-slate-400 space-y-1 pt-3 border-t border-slate-200">
              <p>Posted: {formatDate(review.created_at)}</p>
              {review.updated_at && review.updated_at !== review.created_at && (
                <p>Last updated: {formatDate(review.updated_at)}</p>
              )}
            </div>
          </div>

        ) : (
          /* ── EDIT MODE ── */
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto flex flex-col">
            <div className="flex-1 px-6 py-5 space-y-5">

              {/* Star Rating */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                  Star Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        onClick={() => setRating(star)}
                        className="p-0.5 transition-transform hover:scale-110"
                      >
                        <Star
                          size={26}
                          className={
                            star <= displayRating
                              ? 'fill-amber-400 text-amber-400'
                              : 'fill-slate-100 text-slate-300'
                          }
                        />
                      </button>
                    ))}
                  </div>
                  {displayRating > 0 && (
                    <span className="text-[12px] font-medium text-slate-600">
                      {ratingLabels[displayRating]}
                    </span>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={255}
                  placeholder="Summarise your experience"
                  className="w-full px-3 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-800 placeholder-slate-400"
                />
                <p className="text-[11px] text-slate-400 mt-1">{title.length}/255 characters</p>
              </div>

              {/* Review Content */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                  Your Review <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  placeholder="Share the details of your experience — what was great, what could be improved..."
                  className="w-full px-3 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-800 placeholder-slate-400 resize-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">{content.length}/5000 characters</p>
              </div>

              {/* Experience Date */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                  Experience Date{' '}
                  <span className="text-[11px] text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="date"
                  value={experienceDate}
                  onChange={(e) => setExperienceDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 text-[13px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-800"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-[12px] text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="shrink-0 flex items-center gap-3 px-6 py-4 border-t border-slate-200 bg-white">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[12px] font-medium text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="ml-auto inline-flex items-center gap-2 px-5 py-2 text-[13px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving…
                  </>
                ) : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default ConsumerReviewDrawer;
